// components/filters/LocationFilterModal.tsx
import React, {useEffect, useMemo, useState} from 'react';
import {Input, Spin} from 'antd';
import styled from 'styled-components';
import {useQuery} from '@tanstack/react-query';
import {ILocation, Locations} from "../../../services/types.ts";
import {getSearchLocation} from "../../../services/api.ts";
import {useDebounce} from "../hooks/useDebounce.ts";
import {ModalComponent} from "../ModalComponent.tsx";
import {WhatOption} from "../../../components/ProfessionModal.tsx";
import {colors} from "../../../util/colors.ts";
import {defaultWhereOptions} from "../../../util/utils.ts";

interface LocationFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (selected: ILocation | null) => void;
    initialSelectedLocation: ILocation | null;
    isCandidate?: boolean;
}

export const LocationFilterModal: React.FC<LocationFilterModalProps> = ({
                                                                            isOpen,
                                                                            onClose,
                                                                            onSave,
                                                                            initialSelectedLocation,
                                                                            isCandidate
                                                                        }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [tempSelected, setTempSelected] = useState<ILocation | null>(initialSelectedLocation);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const {data: locationsData = [], isFetching} = useQuery<Locations, Error>({
        queryKey: ['searchLocation', debouncedSearchTerm],
        queryFn: () => getSearchLocation(debouncedSearchTerm),
        enabled: isOpen && !!debouncedSearchTerm, // Only fetch when modal open and search term exists
        staleTime: 1000 * 60 * 5, // Cache results for 5 minutes
    });

    useEffect(() => {
        if (isOpen) {
            setSearchTerm(''); // Clear search on open
            setTempSelected(initialSelectedLocation);
        }
    }, [isOpen, initialSelectedLocation]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    }

    const handleSave = () => {
        onSave(tempSelected);
        onClose();
    };

    const handleCancel = () => {
        setTempSelected(initialSelectedLocation); // Reset to initial on cancel
        onClose();
    };

    const handleSelect = (location: ILocation) => {
        let finalOptionState: ILocation | null = location;
        if (location.label_client === "Alle Regionen und Postleitzahlen") {
            finalOptionState = null;
        } else if (location.label_client === "Homeoffice") {
            finalOptionState = {
                ...location,
                radius: 0,
                region: 2,
            };
        }
        setTempSelected(finalOptionState);
    };

    const memoizedOptions = useMemo(() => {
        return [...defaultWhereOptions, ...locationsData]?.map((option) => (
            <WhatOption
                key={`${option.id}-${isCandidate ? option.label: option.label_client}`}
                onClick={() => handleSelect(option)}
                isSelectedOpt={tempSelected?.id === option.id}
            >
                {isCandidate ? option.label: option.label_client}
            </WhatOption>
        )) ?? [];
    }, [isCandidate, locationsData, tempSelected?.id]);

    return (
        <ModalComponent
            title="Wo suchen Sie?"
            open={isOpen}
            onClose={handleCancel}
            onSave={handleSave}
            footerValue={
                <div style={{textAlign: 'left', fontStyle: tempSelected ? 'normal' : 'italic'}}>
                    {tempSelected?.label_client || "Kein Filter (Alle Standorte)"}
                </div>
            }
        >
            <WhereWrapper>
                <Input
                    size="large"
                    style={{marginBottom: 16}}
                    placeholder="Suche um Postleitzahl (empfohlen) oder in Region"
                    value={searchTerm}
                    onChange={handleSearchChange}
                />
                {isFetching && <Spin style={{display: 'block', margin: '20px auto'}}/>}
                {!isFetching && memoizedOptions.length === 0 && searchTerm && (
                    <NoResults>Keine Ergebnisse für "{searchTerm}"</NoResults>
                )}
                {!isFetching && memoizedOptions.length > 0 && (
                    <OptionsContainer>{memoizedOptions}</OptionsContainer>
                )}
            </WhereWrapper>
        </ModalComponent>
    );
};

const WhereWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 70vh;
`;

const NoResults = styled.div`
    padding: 20px;
    text-align: center;
    color: ${colors.text};
`;

const OptionsContainer = styled.div`
    max-height: 60vh;
    overflow-y: auto;
    margin-top: 8px;

    @media (max-width: 767px) {
        max-height: 40vh;
    }
`;
